import { ChangeDetectorRef, Component, inject, input, model, signal, TemplateRef, viewChild } from "@angular/core";
import { Provider } from "../../../models/provider.model";
import { Client } from "../../../models/client.model";
import { MatButtonModule, MatMiniFabButton } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ControlValueAccessor, FormBuilder, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatInputModule } from "@angular/material/input";
import { BillingInfoService } from "../../../services/billing-info.service";
import { catchError, EMPTY } from "rxjs";
import { MatSnackBar } from "@angular/material/snack-bar";
import { HumanPipe } from "../../pipes/human.pipe";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatCardModule } from "@angular/material/card";
import { BillingInfo } from "../../../models/billing-info.model";
import { ConfirmDialogComponent } from "../../../main/confirm-dialog/confirm-dialog.component";
import { MatRadioModule } from "@angular/material/radio";
import { NgTemplateOutlet } from "@angular/common";

type Entity = Provider | Client;

@Component({
  selector: 'app-billing-info-form',
  templateUrl: './billing-info-form.component.html',
  styleUrls: ['./billing-info-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: BillingInfoFormComponent,
    },
  ],
  imports: [
    FormsModule,
    HumanPipe,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMiniFabButton,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatTooltipModule,
    NgTemplateOutlet,
    ReactiveFormsModule,
  ],
})
export class BillingInfoFormComponent implements ControlValueAccessor {
  entity = input.required<Entity>();

  entity_type = input.required<string>();

  public readOnly = input<boolean>(false);

  selectionMode = input<boolean>(false);

  private readonly dialog = inject(MatDialog);

  private readonly fb = inject(FormBuilder);

  form = this.fb.group({
    id: this.fb.control(null),
    city: this.fb.control('', Validators.required),
    email_address: this.fb.control('', [Validators.email]),
    name: this.fb.control('', Validators.required),
    phone_number: this.fb.control(''),
    state: this.fb.control('', Validators.required),
    street_address: this.fb.control('', Validators.required),
    zip_code: this.fb.control(''),
  });

  BillingInfoFormTemplate = viewChild<TemplateRef<any>>('formTemplate');

  saving = signal(false);

  private readonly billingInfoService = inject(BillingInfoService);

  private readonly cdr = inject(ChangeDetectorRef);

  private readonly matDialog = inject(MatDialog);

  private matDialogRef: MatDialogRef<any>;

  private readonly snackBar = inject(MatSnackBar);

  selectedBillingInfo = model<BillingInfo>();


  onChange = (billing_info: BillingInfo) => {};

  onTouched = () => {};

  writeValue(billing_info: BillingInfo) {
    this.form.patchValue(billing_info);
    this.selectedBillingInfo.set(billing_info);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  prefillFormWithEntityData() {
    const data = this.entity();
    this.form.patchValue({
      street_address: data.address?.trim(),
      email_address: data.email?.trim(),
      phone_number: data.phone?.trim(),
      city: data["city"]?.trim(),
    });   
  }

  openBillingInfoForm(billing_info?: BillingInfo) {
    if (billing_info) {
      this.form.patchValue(billing_info);
    }

    this.matDialogRef = this.dialog.open(this.BillingInfoFormTemplate(), { width: '80%' });

    this.matDialogRef.afterClosed().subscribe(() => {
      this.form.reset();
    })
  }

  onSelectBillingInfo(billing_info) {
    this.onChange(billing_info);
  }

  saveBillingInfo() {
    this.saving.set(true);

    const data = {
      ...this.form.value,
      entity_id: this.entity().id,
      entity_type: this.entity_type(),
    }

    let action;
    if (data.id) {
      action = this.billingInfoService.update(data.id, data )
    } else {
      action = this.billingInfoService.create({ billing_info: data });
    }

    action
      .pipe(
        catchError(() => {
          this.saving.set(false);
          this.snackBar.open('Error al guardar Información de Facturación', 'Cerrar', {
            duration: 3000,
            panelClass: 'snackbar-error',
          });
          return EMPTY;
        }),
      )
    .subscribe((billing_info: BillingInfo) => {
      this.saving.set(false);
      this.snackBar.open('Información de Facturación guardada', 'Cerrar', {
        duration: 3000,
        panelClass: 'snackbar-success',
      });

      const index = this.entity().billing_infos.findIndex((info) => info.id === billing_info.id);
      this.cdr.markForCheck();
      if (index !== -1) {
        this.entity().billing_infos[index] = billing_info;
      } else {
        this.entity().billing_infos.push(billing_info);
      }

      this.form.reset();
      this.matDialogRef.close();
    })
  }

  deleteBillingInfo(id: number) {
   

    this.matDialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Eliminar Dirección de Facturación',
        body: '¿Está seguro de que desea eliminar esta Dirección de Facturación?',
      },
    }).afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.saving.set(true);

      this.billingInfoService.destroy(id)
      .pipe(
        catchError(() => {
          this.saving.set(false);
          this.snackBar.open('Error al eliminar Información de Facturación', 'Cerrar', {
            duration: 3000,
            panelClass: 'snackbar-error',
          });
          return EMPTY;
        }),
      )
      .subscribe(() => {
        this.saving.set(false);

        this.snackBar.open('Información de Facturación eliminada', 'Cerrar', {
          duration: 3000,
          panelClass: 'snackbar-success',
        });
        const index = this.entity().billing_infos.findIndex((info) => info.id === id);
        this.cdr.markForCheck();
        if (index !== -1) {
          this.entity().billing_infos.splice(index, 1);
        }
        this.form.reset();
        this.matDialogRef?.close();
      });

    });
  }

}
